import { INK_CHANNELS, type InkChannel } from "@/shared/constants/chartColors";
import type { PrintVolumeGroupKey, PrintVolumePoint } from "./types";

/**
 * Deterministic mock dataset (no CubeJS backend for this widget): the FULL
 * 2026 year of daily print volumes per channel — quieter weekends, a
 * seasonal wave and seeded jitter — bucketed by the requested granularity
 * (day / week / month, OEE mock parity). The chart shows a fixed window of
 * buckets at a time (30 / 26 / 6 bars, sized by the dataZoom in config.ts)
 * and pans across the full year by scrolling — no load-more. Stable across
 * reloads. Swapping in the real API is a body-only change in `state.ts`.
 */

const DAY_MS = 86_400_000;
/** Fixed mock year: the dataset always covers Jan 1 – Dec 31 of 2026. */
const MOCK_YEAR = 2026;

/** Base per-day volume per channel (K dominates, MICR is niche). */
const CHANNEL_DAILY_BASE: Record<InkChannel, number> = {
  C: 140,
  M: 130,
  Y: 155,
  K: 205,
  MICR: 30,
};

/** Deterministic PRNG (LCG) so the fake data is stable across renders. */
function makeRand(seed = 20260918) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Monday of the week containing `date`. */
function mondayOf(date: Date): Date {
  const monday = new Date(date);
  monday.setDate(date.getDate() - ((date.getDay() + 6) % 7));
  return monday;
}

/** Readable bucket label per granularity (unique across the 2026 span). */
function periodLabel(date: Date, group: PrintVolumeGroupKey): string {
  if (group === "month") {
    return `${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  }
  if (group === "week") {
    // Week starting Monday, labeled by its monday (OEE week parity) — a
    // bare `W##` would collide across the year boundary (W53 of 2025 vs
    // W53 of 2026).
    const monday = mondayOf(date);
    return `${pad(monday.getDate())}/${pad(monday.getMonth() + 1)}`;
  }
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
}

/**
 * Full grouped series for the requested granularity, oldest → newest: every
 * day of 2026 summed into day / week / month buckets (365 daily / 53 weekly
 * / 12 monthly buckets). Volumes sum within a bucket.
 */
export function generatePrintVolumes(
  group: PrintVolumeGroupKey = "month",
): PrintVolumePoint[] {
  const rand = makeRand();
  const buckets = new Map<string, PrintVolumePoint>();

  const date = new Date(MOCK_YEAR, 0, 1);
  const end = new Date(MOCK_YEAR, 11, 31);
  while (date.getTime() <= end.getTime()) {
    const weekday = date.getDay() === 0 || date.getDay() === 6 ? 0.55 : 1;
    const season = 1 + 0.12 * Math.sin((2 * Math.PI * date.getTime()) / (365 * DAY_MS));
    const key = periodLabel(date, group);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { period: key, C: 0, M: 0, Y: 0, K: 0, MICR: 0 };
      buckets.set(key, bucket);
    }
    for (const channel of INK_CHANNELS) {
      const jitter = 0.85 + rand() * 0.3;
      bucket[channel] += Math.max(
        0,
        Math.round(CHANNEL_DAILY_BASE[channel] * weekday * season * jitter),
      );
    }
    date.setDate(date.getDate() + 1);
  }

  return [...buckets.values()];
}
